import type { NextRouter } from 'next/router';

import { FlowType } from '@jerry-serverless/types';

import type { AppData } from '../../types/app_data';
import type { Step } from '../types/step';
import { StepCategory } from '../../types/app_data';
import { AddMaintenanceReminderSteps } from '../../types/maintenance_reminder';
import { hasGarageCarsToSetupMaintenanceReminders } from '../../utils/app_data';
import { shouldShowMaintenanceReminderSetup } from '../../utils/maintenance_reminders';

/**
 * Check if the car is electric type.
 */
const isElectricCar = (appData: AppData): boolean => {
  return !!appData.currentAddReminderCar?.isElectric;
};

/**
 * Check 'hasOwnData' for current maintenance reminder steps.
 */
const checkHasOwnData = (appData: AppData, router: NextRouter, field: string) => {
  const carIdFromQuery = router.query.carId;
  const car = appData.selectedCars?.find((car) => car.id === carIdFromQuery) || appData.selectedCars?.[0];
  if (!car) return false;
  return !!appData.maintenanceReminderData?.[car.id]?.[field];
};

/**
 * Returns current id as query param of router.push.
 */
const queryParams = (appData: AppData, router: NextRouter) => {
  return { carId: appData?.currentAddReminderCar?.id || router.query.carId || appData?.selectedCars?.[0]?.id || null };
};

interface GetAddMaintenanceReminderStepsOptions {
  /**
   * Category of the flow.
   */
  flowCategory?: StepCategory;

  /**
   * Callback to determine in which flow should the steps show.
   * This will be used in `Step.canShow`, along with each step's own rules.
   */
  flowFilter: Step['canShow'];

  /**
   * Callback to determine whether to skip the first prompt step.
   */
  skipFirstPromptStep?: Step['canShow'];
}

/**
 * Get list of adding car steps for /policy flow (cars & drivers on same page).
 */
export function getAddMaintenanceReminderSteps({
  flowCategory = StepCategory.Reminder,
  flowFilter,
  skipFirstPromptStep,
}: GetAddMaintenanceReminderStepsOptions): Step[] {
  const promptStep: Step = {
    id: 'maintenancePrompt',
    pathname: '/maintenanceprompt',
    canShow: (appData) => {
      if (!flowFilter(appData)) return false;

      if (
        appData.flowType &&
        [FlowType.CarValue, FlowType.CarRecall, FlowType.Maintenance].includes(appData.flowType)
      ) {
        return false;
      }

      if (
        // if all cars have not finished maintenance reminder setup, it is the first prompt step
        !appData?.MaintenanceReminderSettledCars?.size &&
        // skip first prompt step as needed
        skipFirstPromptStep?.(appData)
      ) {
        return false;
      }

      return true;
    },
    canShowSkipIdCheck: true,
    hasData: () => true,
    hasOwnData: (appData: AppData) => {
      // If user skip maintenance reminder setup || choose a car to setup
      if (appData.maintenanceAccepted === false || appData.currentAddReminderCar) {
        return true;
      }
      return false;
    },
    allowBackToSelf: true,
    category: () => flowCategory,
  };

  /** Common canShow rules */
  const canShow: Step['canShow'] = (appData): boolean =>
    shouldShowMaintenanceReminderSetup(appData) && flowFilter(appData);

  const maintenanceSteps: Step[] = [
    {
      id: AddMaintenanceReminderSteps.OilChange,
      pathname: '/maintenanceoilchange',
      excludeFromProgressCalculation: true,
      canShow: (appData) => canShow(appData) && !isElectricCar(appData),
      canShowSkipIdCheck: true,
      hasData: () => true,
      hasOwnData: (appData: AppData, router: NextRouter) => {
        return checkHasOwnData(appData, router, 'oilChangeLastCompletedTime');
      },
      queryParams,
      category: () => flowCategory,
    },
    {
      id: AddMaintenanceReminderSteps.OilType,
      pathname: '/maintenanceoiltype',
      excludeFromProgressCalculation: true,
      canShow: (appData) => canShow(appData) && !isElectricCar(appData),
      canShowSkipIdCheck: true,
      hasData: () => true,
      hasOwnData: (appData: AppData, router: NextRouter) => {
        return checkHasOwnData(appData, router, 'oilType');
      },
      queryParams,
      category: () => flowCategory,
    },
    {
      id: AddMaintenanceReminderSteps.TireRotated,
      pathname: '/maintenancetirerotated',
      excludeFromProgressCalculation: true,
      canShow,
      canShowSkipIdCheck: true,
      hasData: () => true,
      hasOwnData: (appData: AppData, router: NextRouter) => {
        return checkHasOwnData(appData, router, 'tireRotatedLastCompletedTime');
      },
      queryParams,
      category: () => flowCategory,
    },
    {
      id: AddMaintenanceReminderSteps.BrakePad,
      pathname: '/maintenancebrakepad',
      excludeFromProgressCalculation: true,
      canShow,
      canShowSkipIdCheck: true,
      hasData: () => true,
      hasOwnData: (appData: AppData, router: NextRouter) => {
        return checkHasOwnData(appData, router, 'brakePadsLastCompletedTime');
      },
      specialNextStep: {
        /**
         * enable: when there are some cars not setup maintenance reminder, next page go to maintenance prompt.
         */
        enable: (appData) => hasGarageCarsToSetupMaintenanceReminders(appData),
        nextStepId: 'maintenancePrompt',
      },
      queryParams,
      category: () => flowCategory,
    },
  ];

  return [promptStep, ...maintenanceSteps];
}
